/**
 * Export API client.
 *
 * Asynchronous throughout. A request returns an id, and the screen polls until a terminal state —
 * a render is the most expensive thing the system does, and holding a request open for it would tie
 * up an API worker (docs/engineering/03-lifecycle-and-states.md).
 */

import type { LaunchLanguage } from '@pius/contracts';

import { request } from '../../api/client';

export type ExportFormat = 'pdf' | 'html' | 'text' | 'ics' | 'json';
export type ExportState = 'pending' | 'rendering' | 'completed' | 'failed';

export interface ExportProfile {
  id: string;
  name: string;
  sections: string[];
}

export interface ExportRequest {
  id: string;
  format: ExportFormat;
  language_code: LaunchLanguage;
  profile_name: string | null;
  state: ExportState;
  failure_reason: string | null;
  created_at: string;
  completed_at: string | null;
  byte_size: number | null;
  expires_at: string | null;
  /** Completed *and* unexpired. The client never works this out for itself. */
  available: boolean;
}

export function fetchProfiles(): Promise<{ data: ExportProfile[] }> {
  return request('/export-profiles');
}

export function fetchExports(liturgyId: string): Promise<{ data: ExportRequest[] }> {
  return request(`/liturgies/${liturgyId}/exports`);
}

export function requestExport(
  liturgyId: string,
  input: { profile_id: string | null; format: ExportFormat; language_code: LaunchLanguage },
): Promise<{ id: string; state: ExportState }> {
  return request(`/liturgies/${liturgyId}/exports`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function exportUrl(liturgyId: string, exportId: string): string {
  return `/api/v1/liturgies/${liturgyId}/exports/${exportId}/content`;
}

/** Whether polling could still change the answer. */
export function isRunning(state: ExportState): boolean {
  return state === 'pending' || state === 'rendering';
}
